import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sparkles, Phone, X } from "lucide-react";
import { toast } from "sonner";

const DELAY_MS = 2 * 60 * 1000; // 2 minutes in a single session
const DISMISS_KEY = "sf_signup_modal_dismissed_at";
const SIGNED_UP_KEY = "sf_signed_up";
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const recentlyDismissed = () => {
  const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return ts > 0 && Date.now() - ts < SEVEN_DAYS_MS;
};

export default function SignupModal() {
  const { user, loading, loginWithToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [mockOtp, setMockOtp] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Never schedule if auth still resolving, already logged in, already signed up, or recently dismissed
    if (loading || user) return;
    if (localStorage.getItem(SIGNED_UP_KEY) === "1" || recentlyDismissed()) return;

    const timer = setTimeout(() => {
      // Re-check conditions at fire time (user may have logged in meanwhile)
      if (localStorage.getItem(SIGNED_UP_KEY) === "1" || recentlyDismissed()) return;
      setOpen(true);
    }, DELAY_MS);

    return () => clearTimeout(timer);
  }, [loading, user]);

  const close = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setOpen(false);
  };

  const sendOtp = async () => {
    if (!phone || phone.length < 10) { toast.error("Enter a valid phone"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/otp/request", { phone });
      setOtpSent(true);
      setMockOtp(r.data.mock_otp || "");
      if (r.data.is_mocked) toast.info("OTP (mock)", { description: `Use ${r.data.mock_otp}` });
      else toast.success("OTP sent via SMS", { description: r.data.phone });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send OTP"); }
    finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/otp/verify", { phone, otp });
      loginWithToken(r.data.token, r.data.user, { needsEmail: !!r.data.needs_email });
      localStorage.setItem(SIGNED_UP_KEY, "1");
      toast.success("Welcome to Shine Fabric 💛");
      setOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Invalid OTP"); }
    finally { setBusy(false); }
  };

  if (!open || user) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" data-testid="signup-modal" onClick={close}>
      <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-7 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={close} data-testid="signup-modal-close" aria-label="Close signup"
          className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500">
          <X className="w-4 h-4" />
        </button>
        <div className="w-12 h-12 rounded-2xl brand-gradient-pink flex items-center justify-center text-white shadow-md mb-3">
          <Sparkles className="w-6 h-6" />
        </div>
        <h2 className="font-display text-xl sm:text-2xl font-semibold">Join the Shine Fabric family</h2>
        <p className="text-sm text-slate-600 mt-1">
          Sign up with your mobile number for faster checkout, order tracking, and early access to new drops &amp; offers.
        </p>

        {!otpSent ? (
          <div className="mt-5 space-y-3">
            <div className="relative">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input data-testid="signup-modal-phone" type="tel" placeholder="Phone (10-digit or +91...)"
                value={phone} onChange={(e) => setPhone(e.target.value)} className="pl-11 rounded-xl h-12" />
            </div>
            <Button onClick={sendOtp} disabled={busy} data-testid="signup-modal-send-otp"
              className="w-full rounded-full h-12 brand-gradient-pink text-white border-0 btn-bouncy font-bold uppercase tracking-wider text-sm">
              {busy ? "Sending…" : "Send OTP"}
            </Button>
            <p className="text-[11px] text-slate-500 text-center">A 6-digit OTP will be sent via SMS. Standard rates may apply.</p>
          </div>
        ) : (
          <div className="mt-5 space-y-3">
            <p className="text-xs text-slate-500 text-center">OTP sent to <b>{phone}</b>{mockOtp ? <> · mock <b className="text-pink-500">{mockOtp}</b></> : null}</p>
            <Input data-testid="signup-modal-otp" inputMode="numeric" maxLength={6} placeholder="6-digit OTP"
              value={otp} onChange={(e) => setOtp(e.target.value)} className="rounded-xl h-12 tracking-[0.5em] text-center font-bold" />
            <Button onClick={verifyOtp} disabled={busy} data-testid="signup-modal-verify-otp"
              className="w-full rounded-full h-12 brand-gradient-pink text-white border-0 btn-bouncy font-bold uppercase tracking-wider text-sm">
              {busy ? "Verifying…" : "Verify & Continue"}
            </Button>
            <div className="flex justify-between text-xs">
              <button onClick={() => setOtpSent(false)} data-testid="signup-modal-change-number" className="text-slate-500 hover:text-slate-900">Change number</button>
              <button onClick={sendOtp} disabled={busy} data-testid="signup-modal-resend-otp" className="text-pink-500 font-bold">Resend</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
